import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, fmtR, getToken, defaultExpiryDate, slugifyFilename } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import BranchMultiSelect from "@/components/BranchMultiSelect";
import { toast } from "sonner";
import { Plus, Download, Search, Trash2 } from "lucide-react";

const blankLine = () => ({ name: "", qty_per_use: 1, retail_value: 0 });
const blankForm = () => ({
    type: "item",
    name: "",
    lines: [blankLine()],
    value_amount: 0,
    restrictions: "",
    expiry_date: defaultExpiryDate(12),
    cost_shop_id: "none",
    allowed_branch_ids: [],
    quantity: 1,
    send_email: false,
    customer_email: "",
    email_template_id: "",
});

export default function Vouchers() {
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [costShops, setCostShops] = useState([]);
    const [branches, setBranches] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);

    const [form, setForm] = useState(blankForm());
    const [creating, setCreating] = useState(false);
    const [createdBatch, setCreatedBatch] = useState(null);

    const load = async () => {
        setLoading(true);
        const params = {};
        if (statusFilter !== "all") params.status = statusFilter;
        if (search) params.search = search;
        try {
            const { data } = await api.get("/admin/vouchers", { params });
            setVouchers(data);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        api.get("/admin/cost-shops").then((r) => setCostShops(r.data));
        api.get("/admin/branches").then((r) => setBranches(r.data));
        api.get("/admin/email-templates").then((r) => setEmailTemplates(r.data.templates || []));
    }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    const totalFace = () => {
        if (form.type === "value") return parseFloat(form.value_amount || 0);
        // qty_per_use is now the TOTAL pool for that line (no multiplication by max_uses).
        return form.lines.reduce((sum, l) => sum + (parseInt(l.qty_per_use || 0, 10) * parseFloat(l.retail_value || 0)), 0);
    };

    const updateLine = (idx, patch) => {
        setForm((f) => ({ ...f, lines: f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
    };
    const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }));
    const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

    const onCreate = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const payload = {
                type: form.type,
                name: form.name,
                quantity: parseInt(form.quantity, 10) || 1,
                restrictions: form.restrictions || "",
                cost_shop_id: form.cost_shop_id === "none" ? null : form.cost_shop_id,
                allowed_branch_ids: form.allowed_branch_ids || [],
            };
            if (form.expiry_date) payload.expiry_date = form.expiry_date;

            if (form.type === "item") {
                const cleanLines = form.lines
                    .filter((l) => l.name && l.name.trim() !== "")
                    .map((l) => ({
                        name: l.name.trim(),
                        qty_per_use: parseInt(l.qty_per_use, 10) || 1,
                        retail_value: parseFloat(l.retail_value) || 0,
                    }));
                if (cleanLines.length === 0) {
                    toast.error("Add at least one line item with a name");
                    setCreating(false);
                    return;
                }
                payload.lines = cleanLines;
            } else {
                payload.value_amount = parseFloat(form.value_amount) || 0;
            }

            const { data } = await api.post("/admin/vouchers", payload);
            setCreatedBatch(data);
            toast.success(`Created ${data.created} voucher(s)`);

            // Optionally send each created voucher directly to a customer
            if (form.send_email && form.customer_email && data.vouchers?.length) {
                const emailBody = {
                    to_email: form.customer_email.trim(),
                    email_template_id: form.email_template_id || null,
                };
                const results = await Promise.allSettled(
                    data.vouchers.map(v => api.post(`/admin/vouchers/${v.id}/email`, emailBody))
                );
                const ok = results.filter(r => r.status === "fulfilled").length;
                const failed = results.length - ok;
                if (ok) toast.success(`Emailed ${ok} voucher(s) to ${form.customer_email}`);
                if (failed) toast.error(`${failed} email(s) failed to send`);
            }

            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setCreating(false); }
    };

    const downloadQr = (v, index = null) => {
        const token = getToken();
        fetch(`${api.defaults.baseURL}/admin/vouchers/${v.id}/qr`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then((r) => r.blob()).then((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const base = slugifyFilename(v.name);
            a.download = index !== null ? `${base}-${String(index + 1).padStart(2, "0")}.png` : `${base}.png`;
            a.click();
            URL.revokeObjectURL(url);
        });
    };

    const downloadAllBatch = async () => {
        if (!createdBatch) return;
        for (let i = 0; i < createdBatch.vouchers.length; i++) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 150));
            downloadQr(createdBatch.vouchers[i], i);
        }
    };

    const closeDialog = () => {
        setOpen(false);
        setCreatedBatch(null);
        setForm(blankForm());
    };

    return (
        <div className="p-8 max-w-7xl" data-testid="vouchers-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="text-xs tracking-widest text-[#7A726D] uppercase">Inventory</div>
                    <h1 className="font-display text-4xl font-bold text-espresso tracking-tight">Vouchers</h1>
                </div>
                <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) closeDialog(); }}>
                    <DialogTrigger asChild>
                        <Button className="bg-espresso hover:bg-[#2c1a17] rounded-sm" data-testid="new-voucher-button">
                            <Plus className="w-4 h-4 mr-1" /> New voucher
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="font-display text-espresso">{createdBatch ? "Vouchers created" : "Create voucher"}</DialogTitle></DialogHeader>
                        {createdBatch ? (
                            <div className="space-y-4" data-testid="batch-result">
                                <p className="text-sm text-[#3E2723]">Successfully created <b>{createdBatch.created}</b> voucher(s). Download their QR codes:</p>
                                <div className="max-h-80 overflow-auto border border-[#E5E0D8] rounded-sm">
                                    <Table>
                                        <TableHeader>
                                            <TableRow><TableHead>Serial</TableHead><TableHead className="text-right">Action</TableHead></TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {createdBatch.vouchers.map((v, i) => (
                                                <TableRow key={v.id}>
                                                    <TableCell className="font-mono text-xs">{v.serial}</TableCell>
                                                    <TableCell className="text-right">
                                                        <Button variant="outline" size="sm" onClick={() => downloadQr(v, i)} className="rounded-sm" data-testid={`download-qr-${v.serial}`}>
                                                            <Download className="w-3 h-3 mr-1" /> QR
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <Button variant="outline" onClick={downloadAllBatch} className="rounded-sm" data-testid="download-all-button">
                                        <Download className="w-4 h-4 mr-1" /> Download all
                                    </Button>
                                    <Button onClick={closeDialog} className="bg-espresso rounded-sm" data-testid="batch-done-button">Done</Button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={onCreate} className="space-y-4" data-testid="voucher-create-form">
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Type</Label>
                                        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                            <SelectTrigger className="mt-1 rounded-sm" data-testid="form-type-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="item">Item / Bundle</SelectItem>
                                                <SelectItem value="value">Rand value</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Quantity (bulk)</Label>
                                        <Input type="number" min="1" max="500" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1 rounded-sm" data-testid="form-quantity-input"/>
                                    </div>
                                </div>

                                <div>
                                    <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Voucher name {form.type === "item" && <span className="text-[#7A726D] normal-case">(bundle label)</span>}</Label>
                                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder={form.type === "item" ? "Welcome Bundle" : "R500 Gift voucher"} className="mt-1 rounded-sm" data-testid="form-name-input"/>
                                </div>

                                {form.type === "item" ? (
                                    <div className="border border-[#E5E0D8] rounded-sm p-3 bg-[#F9F8F6]">
                                        <div className="flex items-center justify-between mb-2">
                                            <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Line items</Label>
                                            <Button type="button" variant="outline" size="sm" onClick={addLine} className="rounded-sm h-7" data-testid="add-line-button">
                                                <Plus className="w-3 h-3 mr-1" /> Add item
                                            </Button>
                                        </div>
                                        <div className="space-y-2">
                                            {form.lines.map((line, idx) => (
                                                <div key={idx} className="grid grid-cols-[1fr_70px_90px_30px] gap-2 items-center" data-testid={`line-row-${idx}`}>
                                                    <Input
                                                        value={line.name}
                                                        onChange={(e) => updateLine(idx, { name: e.target.value })}
                                                        placeholder="Item name"
                                                        className="rounded-sm h-9"
                                                        data-testid={`line-name-${idx}`}
                                                    />
                                                    <Input
                                                        type="number" min="1"
                                                        value={line.qty_per_use}
                                                        onChange={(e) => updateLine(idx, { qty_per_use: e.target.value })}
                                                        placeholder="Total qty"
                                                        className="rounded-sm h-9"
                                                        data-testid={`line-qty-${idx}`}
                                                    />
                                                    <Input
                                                        type="number" min="0" step="0.01"
                                                        value={line.retail_value}
                                                        onChange={(e) => updateLine(idx, { retail_value: e.target.value })}
                                                        placeholder="R0.00"
                                                        className="rounded-sm h-9"
                                                        data-testid={`line-value-${idx}`}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeLine(idx)}
                                                        disabled={form.lines.length === 1}
                                                        className="text-[#B24A45] disabled:opacity-30 disabled:cursor-not-allowed"
                                                        data-testid={`line-remove-${idx}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="grid grid-cols-[1fr_70px_90px_30px] gap-2 mt-2 text-[10px] uppercase tracking-wide text-[#7A726D]">
                                            <span>Name</span><span>Qty / use</span><span>Retail R</span><span></span>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Rand value</Label>
                                        <Input type="number" step="0.01" min="0" value={form.value_amount} onChange={(e) => setForm({ ...form, value_amount: e.target.value })} className="mt-1 rounded-sm" required data-testid="form-value-amount-input"/>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 gap-3">
                                    <div className="bg-[#F3EFEA] rounded-sm p-2 flex flex-col justify-center">
                                        <div className="text-[10px] uppercase tracking-wide text-[#7A726D]">Total face value</div>
                                        <div className="font-display text-xl font-bold text-espresso" data-testid="total-face-value">{fmtR(totalFace())}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Expiry date</Label>
                                        <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="mt-1 rounded-sm" data-testid="form-expiry-input"/>
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Cost shop</Label>
                                        <Select value={form.cost_shop_id} onValueChange={(v) => setForm({ ...form, cost_shop_id: v })}>
                                            <SelectTrigger className="mt-1 rounded-sm" data-testid="form-cost-shop-select"><SelectValue placeholder="Optional"/></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">— None —</SelectItem>
                                                {costShops.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Redeemable at</Label>
                                    <BranchMultiSelect
                                        branches={branches}
                                        value={form.allowed_branch_ids}
                                        onChange={(v) => setForm({ ...form, allowed_branch_ids: v })}
                                        testid="form-branches-select"
                                    />
                                    <div className="text-xs text-[#7A726D] mt-1">Leave empty to allow all branches.</div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Restrictions</Label>
                                    <Textarea value={form.restrictions} onChange={(e) => setForm({ ...form, restrictions: e.target.value })} placeholder="e.g. Valid weekdays only" className="mt-1 rounded-sm" data-testid="form-restrictions-input"/>
                                </div>

                                <div className="border border-[#E5E0D8] rounded-sm p-3 bg-[#FDFCFA]">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={form.send_email}
                                            onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                                            data-testid="form-send-email-checkbox"
                                        />
                                        <span className="text-sm font-medium text-espresso">Also email this voucher to a customer</span>
                                    </label>
                                    {form.send_email && (
                                        <div className="mt-3 grid grid-cols-2 gap-3">
                                            <div>
                                                <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Customer email</Label>
                                                <Input
                                                    type="email"
                                                    value={form.customer_email}
                                                    onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                                                    placeholder="customer@example.com"
                                                    className="mt-1 rounded-sm"
                                                    data-testid="form-customer-email-input"
                                                    required={form.send_email}
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-xs uppercase tracking-wide text-[#7A726D]">Email template</Label>
                                                <Select
                                                    value={form.email_template_id || "default"}
                                                    onValueChange={(v) => setForm({ ...form, email_template_id: v === "default" ? "" : v })}
                                                >
                                                    <SelectTrigger className="mt-1 rounded-sm" data-testid="form-email-template-select"><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="default">Use default template</SelectItem>
                                                        {emailTemplates.map((t) => (
                                                            <SelectItem key={t.id} value={t.id}>
                                                                {t.name}{t.is_default ? " (current default)" : ""}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            {parseInt(form.quantity, 10) > 1 && (
                                                <div className="col-span-2 text-xs text-[#B24A45]">
                                                    ⚠ You&apos;re creating {form.quantity} vouchers — all of them will be emailed to this address.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <Button type="submit" disabled={creating} className="w-full bg-espresso hover:bg-[#2c1a17] rounded-sm h-11 font-display" data-testid="form-submit-button">
                                    {creating ? "Creating…" : `Create ${form.quantity || 1} voucher(s)${form.send_email ? " & email" : ""}`}
                                </Button>
                            </form>
                        )}
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex gap-3 mb-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A726D]" />
                    <Input
                        placeholder="Search by serial or name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        className="pl-9 rounded-sm border-[#E5E0D8] bg-white"
                        data-testid="vouchers-search-input"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-44 rounded-sm border-[#E5E0D8] bg-white" data-testid="vouchers-status-filter"><SelectValue/></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="redeemed">Redeemed</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                        <SelectItem value="void">Void</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <Card className="border-[#E5E0D8] shadow-none rounded-sm">
                <CardContent className="p-0">
                    <Table data-testid="vouchers-table">
                        <TableHeader>
                            <TableRow className="bg-[#F9F8F6]">
                                <TableHead>Serial</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Items / Value</TableHead>
                                <TableHead>Remaining</TableHead>
                                <TableHead>Expiry</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && <TableRow><TableCell colSpan={8} className="text-center text-[#7A726D] py-8">Loading…</TableCell></TableRow>}
                            {!loading && vouchers.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-[#7A726D] py-8">No vouchers</TableCell></TableRow>}
                            {vouchers.map((v) => (
                                <TableRow key={v.id} className="hover:bg-[#F9F8F6]" data-testid={`voucher-row-${v.serial}`}>
                                    <TableCell className="font-mono text-xs">{v.serial}</TableCell>
                                    <TableCell>{v.name}</TableCell>
                                    <TableCell className="capitalize">{v.type === "item" ? "Bundle" : "Value"}</TableCell>
                                    <TableCell className="text-xs">
                                        {v.type === "value"
                                            ? `${fmtR(v.remaining_value)} / ${fmtR(v.value_amount)}`
                                            : (v.lines || []).length === 0
                                                ? "—"
                                                : `${(v.lines || []).length} line(s) · ${fmtR(v.remaining_value)} / ${fmtR(v.value_amount)}`}
                                    </TableCell>
                                    <TableCell className="text-xs">
                                        {v.type === "value"
                                            ? fmtR(v.remaining_value)
                                            : `${(v.lines || []).reduce((s, l) => s + l.qty_remaining, 0)} / ${(v.lines || []).reduce((s, l) => s + l.qty_per_use, 0)} items`}
                                    </TableCell>
                                    <TableCell>{v.expiry_date ? v.expiry_date.slice(0, 10) : "—"}</TableCell>
                                    <TableCell><StatusBadge status={v.status} /></TableCell>
                                    <TableCell className="text-right">
                                        <Link to={`/admin/vouchers/${v.id}`} className="text-[#C48464] hover:underline text-sm" data-testid={`view-voucher-${v.serial}`}>View →</Link>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
